'use client'

import { Button } from '@/core/components/form/button/button'
import { useEffect, useState } from 'react'

import { cn } from '@/core/utils/common'

import { msUntilMidnight } from '@/features/veluvit/lib/attendance'
import { countdown } from '@/features/veluvit/lib/format'
import { useAppNow } from '@/features/veluvit/hooks/use-app-now'

import type { ButtonHTMLAttributes, ReactNode } from 'react'

/* 레퍼런스 공통 조각(.badge .stage .chip .toggle .ringp …). 모두 짧고 서로 붙어 쓰여 한 파일에 둔다. */

const BADGE: Record<string, string> = {
  NEW: 'bg-coral text-inverse',
  adult: 'border border-coral bg-background text-coral',
  EPp: 'bg-ok text-[#0b1a12]',
  MULTI: 'bg-sub text-gem-ink',
  UPDATED: 'bg-violet text-inverse',
  BEST: 'bg-point text-inverse',
  author: 'bg-violet text-inverse',
  grade: 'border border-line2 bg-foreground/8 font-bold text-t2',
  hon: 'bg-sub text-gem-ink',
}

function badgeKey(label: string) {
  return label === 'EP+' ? 'EPp' : label === '19+' ? 'adult' : label
}

export function Badge({
  label,
  kind,
  className,
}: {
  label: ReactNode
  kind?: string
  className?: string
}) {
  const key = kind || (typeof label === 'string' ? badgeKey(label) : 'grade')
  return (
    <span
      className={cn(
        'inline-flex h-4.5 items-center rounded-control px-1.25 text-c10 leading-none font-extrabold tracking-[0.02em]',
        BADGE[key] || 'bg-black/55 text-t1 backdrop-blur-md',
        className
      )}
    >
      {label}
    </span>
  )
}

export function Chip({
  on,
  gold,
  className,
  children,
  ...rest
}: {
  on?: boolean
  gold?: boolean
  className?: string
  children: ReactNode
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <Button
      type="button"
      className={cn(
        'inline-flex h-8 flex-none cursor-pointer items-center gap-1.25 rounded-full border px-3.25 text-c13 leading-none whitespace-nowrap transition-[color,background-color,border-color] duration-150',
        on
          ? // 고른 칩 = 흰 채움 + 검정 글자(design-guide: 코랄 채움은 촌스럽다, 흰색은 칩 · 작은 버튼에만)
            'border-foreground bg-foreground font-semibold text-background'
          : gold
            ? 'border-sub/50 bg-sub/8 text-sub hover:bg-sub/14'
            : 'border-transparent bg-foreground/[0.08] text-t2 hover:bg-foreground/[0.12] hover:text-t1 focus-visible:border-white/40',
        className
      )}
      {...rest}
      variant="bare"
    >
      {children}
    </Button>
  )
}

/** 스위치 모양 표시(상호작용은 부모 행이 맡는다) */
export function ToggleMark({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative h-7 w-11.5 flex-none rounded-panel transition-colors duration-200',
        on ? 'bg-violet' : 'bg-surface-strong',
        "after:absolute after:top-0.75 after:left-0.75 after:size-5.5 after:rounded-full after:bg-white after:transition-transform after:duration-200 after:ease-smooth after:content-['']",
        on && 'after:translate-x-4.5'
      )}
    />
  )
}

/* ---------- 카운트다운 ----------
   레퍼런스 S.cd — 첫 로드 시각 기준 고정 마감(오늘의 딜 23:12:05, 첫 충전 6일 23:56:31) */
function useSecondTick() {
  const initialNow = useAppNow()
  const [now, setNow] = useState(initialNow)
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])
  return now
}

/** 오늘 자정까지 — 출석을 다시 받을 수 있는 시각 */
export function MidnightCountdown() {
  const now = useSecondTick()
  return <span className="tabular-nums">{countdown(msUntilMidnight(now))}</span>
}
